import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SummaryCard from '../components/SummaryCard';
import { SpendingDoughnut, BalanceTrendLine } from '../components/Charts';
import {
  FaWallet, FaArrowUp, FaArrowDown, FaArrowRight,
  FaExclamationCircle, FaPlus, FaFire
} from 'react-icons/fa';

const categoryEmoji = {
  Food: '🍔', Salary: '💼', Utilities: '⚡', Electronics: '💻',
  Health: '💪', Transport: '🚗', Housing: '🏠', Entertainment: '🎬',
  Healthcare: '🏥', Shopping: '🛍️', Investment: '📈', Other: '📁',
};

const CATEGORY_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const Dashboard = () => {
  const { transactions, role, formatCurrency } = useApp();

  const summary = useMemo(() => {
    const income = transactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { income, expense, balance: income - expense };
  }, [transactions]);

  const topCategories = useMemo(() => {
    const map = {};
    transactions.filter(t => t.type === 'expense').forEach(t => {
      map[t.category] = (map[t.category] || 0) + t.amount;
    });
    return Object.entries(map)
      .map(([category, amount]) => ({ category, amount: parseFloat(amount.toFixed(2)) }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
  }, [transactions]);

  const recentTransactions = useMemo(() =>
    [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6),
    [transactions]
  );

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Your financial overview at a glance</p>
        </div>
        {role === 'admin' ? (
          <Link to="/add-transaction" className="btn btn-primary">
            <FaPlus size={12} /> Add Transaction
          </Link>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.55rem 1rem',
            background: 'var(--accent-light)',
            backdropFilter: 'blur(8px)',
            border: '1px solid var(--border-glow)',
            borderRadius: '12px',
            color: 'var(--accent)',
            fontSize: '0.8rem', fontWeight: 600,
          }}>
            <FaExclamationCircle size={12} /> Viewer mode — read only
          </div>
        )}
      </div>

      {/* ── Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem' }}>
        <SummaryCard
          title="Total Income" amount={summary.income} change={5.2}
          icon={FaArrowUp} variant="income"
          subtitle={`${transactions.filter(t => t.type === 'income').length} transactions`}
        />
        <SummaryCard
          title="Total Expenses" amount={summary.expense} change={2.7}
          icon={FaArrowDown} variant="expense"
          subtitle={`${transactions.filter(t => t.type === 'expense').length} transactions`}
        />
        <SummaryCard
          title="Net Balance" amount={summary.balance} change={3.8}
          icon={FaWallet} variant="balance"
          subtitle={summary.balance >= 0 ? '↑ Positive cash flow' : '↓ Negative cash flow'}
        />
      </div>

      {/* ── Charts Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.25rem' }}>

        {/* Balance Trend */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <p className="section-title">Balance Trend</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Income vs expenses over time</p>
            </div>
            <span className="section-badge">All Time</span>
          </div>
          <BalanceTrendLine transactions={transactions} />
        </div>

        {/* Spending Breakdown */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <p className="section-title">Spending Breakdown</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>By category</p>
            </div>
            <span className="section-badge">Expenses</span>
          </div>
          <SpendingDoughnut data={topCategories} />
        </div>
      </div>

      {/* ── Bottom Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.25rem' }}>

        {/* Recent Transactions */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div>
              <p className="section-title">Recent Transactions</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Latest activity</p>
            </div>
            <Link to="/transactions" style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              fontSize: '0.8rem', fontWeight: 600,
              color: 'var(--accent)', textDecoration: 'none',
              padding: '0.35rem 0.75rem', borderRadius: '9px',
              background: 'var(--accent-light)',
              border: '1px solid var(--border-glow)',
              transition: 'all 0.2s',
            }}>
              View all <FaArrowRight size={10} />
            </Link>
          </div>

          {recentTransactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💸</div>
              <p style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>No transactions yet</p>
              <p style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>Add your first transaction to get started</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {recentTransactions.map((tx, i) => (
                <div
                  key={tx.id}
                  className="tx-item slide-in"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <div className={`tx-icon ${tx.type === 'income' ? 'tx-icon-income' : 'tx-icon-expense'}`}>
                      {categoryEmoji[tx.category] || '📁'}
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.1rem' }}>
                        {tx.title}
                      </p>
                      <p style={{ fontSize: '0.73rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                        {tx.category} · {formatDate(tx.date)}
                      </p>
                    </div>
                  </div>
                  <span style={{
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    color: tx.type === 'income' ? 'var(--success)' : 'var(--danger)',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                    marginLeft: '0.5rem',
                  }}>
                    {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Spending Categories */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div>
              <p className="section-title">Top Spending</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Highest expense categories</p>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              fontSize: '0.75rem', fontWeight: 700,
              color: 'var(--danger)',
              background: 'var(--danger-light)',
              border: '1px solid rgba(248,113,113,0.2)',
              padding: '0.3rem 0.7rem', borderRadius: '9px',
            }}>
              <FaFire size={11} /> Hot
            </div>
          </div>

          {topCategories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📊</div>
              <p style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>No expense data yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              {topCategories.map((cat, i) => {
                const maxAmt = topCategories[0].amount;
                const pct = Math.round((cat.amount / maxAmt) * 100);
                const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                return (
                  <div key={cat.category} className="slide-in" style={{ animationDelay: `${i * 0.07}s` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                        {/* Rank badge */}
                        <span style={{
                          width: '22px', height: '22px', borderRadius: '7px',
                          background: i === 0 ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'var(--bg-secondary)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.65rem', fontWeight: 800,
                          color: i === 0 ? 'white' : 'var(--text-muted)',
                          border: i === 0 ? 'none' : 'var(--glass-border)',
                        }}>
                          {i + 1}
                        </span>
                        <span style={{ fontSize: '1rem' }}>{categoryEmoji[cat.category] || '📁'}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {cat.category}
                        </span>
                      </div>
                      <span style={{ fontSize: '0.875rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                        {formatCurrency(cat.amount)}
                      </span>
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${color}99, ${color})`,
                          animationDelay: `${i * 0.1}s`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
